package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.model.PosItem
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

data class CategoryMeta(val name: String, val displayName: String, val icon: ImageVector, val color: Color)

val menuCategories = listOf(
    CategoryMeta("ALL", "All Items", Icons.Default.RestaurantMenu, Color(0xFF1E3A8A)),
    CategoryMeta("Food", "Food", Icons.Default.LunchDining, Color(0xFFD97706)),
    CategoryMeta("Beverages", "Beverages", Icons.Default.LocalBar, Color(0xFF0284C7)),
    CategoryMeta("Desserts", "Desserts", Icons.Default.Cake, Color(0xFFEC4899)),
    CategoryMeta("Alcohol", "Alcohol", Icons.Default.WineBar, Color(0xFF7C3AED)),
    CategoryMeta("Other", "Other", Icons.Default.Category, Color(0xFF64748B))
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminMenuManagementScreen(
    onNavigateBack: () -> Unit = {}
) {
    val viewModel = koinViewModel<AdminMenuViewModel>()
    val categories by viewModel.categories.collectAsState()
    val items by viewModel.items.collectAsState()

    var searchQuery by remember { mutableStateOf("") }
    var selectedCategory by remember { mutableStateOf("ALL") }
    var showAddItemDialog by remember { mutableStateOf(false) }

    // Filter items
    val filteredItems = remember(items, categories, searchQuery, selectedCategory) {
        items.filter { item ->
            val cat = categories.find { it.id == item.category_id }
            val catName = cat?.name ?: "Other"
            
            val matchesCategory = if (selectedCategory == "ALL") true else catName.equals(selectedCategory, ignoreCase = true)
            val matchesSearch = searchQuery.isBlank() || 
                    item.name.contains(searchQuery, ignoreCase = true) || 
                    catName.contains(searchQuery, ignoreCase = true)
            
            matchesCategory && matchesSearch
        }
    }

    if (showAddItemDialog) {
        var nameInput by remember { mutableStateOf("") }
        var categoryInput by remember { mutableStateOf("Food") }
        var priceInput by remember { mutableStateOf("") }
        var isAvailableInput by remember { mutableStateOf(true) }

        AlertDialog(
            onDismissRequest = { showAddItemDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = { Text(l10n("Yeni Yemek / Ürün Ekle"), fontWeight = FontWeight.Bold) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    OutlinedTextField(
                        value = nameInput,
                        onValueChange = { nameInput = it },
                        label = { Text(l10n("Yemek / Ürün Adı")) },
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Text(l10n("Kategori Seçin:"), fontWeight = FontWeight.Medium, fontSize = 13.sp)
                    val catList = listOf("Food", "Beverages", "Desserts", "Alcohol", "Other")
                    val row1 = catList.take(3)
                    val row2 = catList.drop(3)

                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            row1.forEach { cat ->
                                val isSelected = categoryInput == cat
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .clip(RoundedCornerShape(8.dp))
                                        .background(if (isSelected) Color(0xFF1E3A8A) else Color(0xFFF1F5F9))
                                        .clickable { categoryInput = cat }
                                        .padding(vertical = 8.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        l10n(cat),
                                        fontSize = 12.sp,
                                        color = if (isSelected) Color.White else Color.DarkGray,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                            }
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            row2.forEach { cat ->
                                val isSelected = categoryInput == cat
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .clip(RoundedCornerShape(8.dp))
                                        .background(if (isSelected) Color(0xFF1E3A8A) else Color(0xFFF1F5F9))
                                        .clickable { categoryInput = cat }
                                        .padding(vertical = 8.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        l10n(cat),
                                        fontSize = 12.sp,
                                        color = if (isSelected) Color.White else Color.DarkGray,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }

                    OutlinedTextField(
                        value = priceInput,
                        onValueChange = { priceInput = it },
                        label = { Text("${l10n("Fiyat")} (₽)") },
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(l10n("Satışta / Mevcut"), fontSize = 14.sp)
                        Switch(
                            checked = isAvailableInput,
                            onCheckedChange = { isAvailableInput = it }
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val price = priceInput.toDoubleOrNull()
                        if (nameInput.isNotBlank() && price != null) {
                            viewModel.addMenuItem(nameInput, categoryInput, price, isAvailableInput)
                            showAddItemDialog = false
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF10B981)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(l10n("Menüye Ekle"), fontWeight = FontWeight.Bold, color = Color.White)
                }
            },
            dismissButton = {
                TextButton(onClick = { showAddItemDialog = false }) { Text(l10n("İptal"), color = Color(0xFF64748B)) }
            }
        )
    }

    Scaffold(
        contentWindowInsets = WindowInsets(0, 0, 0, 0)
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC))
        ) {
            // Header Bar - Compact & Horizontal Add Button
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color.White)
                    .padding(horizontal = 12.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f)
                ) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Back"))
                    }
                    Spacer(modifier = Modifier.width(4.dp))
                    Column {
                        Text(com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Menü Yönetimi"), fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color(0xFF0F172A))
                        Text("${items.size} " + com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Ürün Kayıtlı"), fontSize = 11.sp, color = Color.Gray)
                    }
                }

                Button(
                    onClick = { showAddItemDialog = true },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF10B981)),
                    shape = RoundedCornerShape(8.dp),
                    contentPadding = PaddingValues(horizontal = 10.dp, vertical = 6.dp)
                ) {
                    Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("+ Yeni Ürün Ekle"), fontSize = 12.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                }
            }

            HorizontalDivider(color = Color(0xFFE2E8F0))

            Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                // 1. Search Bar at Top
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    placeholder = { Text(com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Menüde yemek, içecek ara...")) },
                    leadingIcon = { Icon(Icons.Default.Search, contentDescription = com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Search"), tint = Color.Gray) },
                    trailingIcon = {
                        if (searchQuery.isNotEmpty()) {
                            IconButton(onClick = { searchQuery = "" }) {
                                Icon(Icons.Default.Clear, contentDescription = com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Temizle"), tint = Color.Gray)
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White
                    ),
                    singleLine = true
                )

                Spacer(modifier = Modifier.height(14.dp))

                // 2. Category Selection Cards
                Text(com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Kategoriler"), fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF334155))
                Spacer(modifier = Modifier.height(8.dp))

                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    items(menuCategories) { catMeta ->
                        val isSelected = selectedCategory == catMeta.name
                        Card(
                            modifier = Modifier
                                .widthIn(min = 100.dp)
                                .clickable { selectedCategory = catMeta.name },
                            colors = CardDefaults.cardColors(
                                containerColor = if (isSelected) catMeta.color else Color.White
                            ),
                            border = BorderStroke(1.dp, if (isSelected) catMeta.color else Color(0xFFCBD5E1)),
                            shape = RoundedCornerShape(10.dp),
                            elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
                        ) {
                            Row(
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    catMeta.icon,
                                    contentDescription = com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n(catMeta.displayName),
                                    tint = if (isSelected) Color.White else catMeta.color,
                                    modifier = Modifier.size(20.dp)
                                )
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(
                                    com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n(catMeta.displayName),
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 13.sp,
                                    color = if (isSelected) Color.White else Color(0xFF1E293B)
                                )
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                // 3. Menu Item List / Grid
                if (filteredItems.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Icon(Icons.Default.Restaurant, contentDescription = null, tint = Color.LightGray, modifier = Modifier.size(48.dp))
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("Eşleşen menü ürünü bulunamadı.", color = Color.Gray, fontSize = 14.sp)
                        }
                    }
                } else {
                    val gridState = androidx.compose.foundation.lazy.grid.rememberLazyGridState()
                    LazyVerticalGrid(
                        state = gridState,
                        columns = GridCells.Adaptive(minSize = 160.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        modifier = Modifier.fillMaxSize().verticalScrollbar(gridState)
                    ) {
                        items(filteredItems) { item ->
                            val cat = categories.find { it.id == item.category_id }
                            val catName = cat?.name ?: "Other"

                            HFCard(
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(12.dp),
                                    verticalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.Top
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                item.name,
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 14.sp,
                                                color = Color(0xFF0F172A),
                                                maxLines = 2,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                            Spacer(modifier = Modifier.height(4.dp))
                                            Box(
                                                modifier = Modifier
                                                    .clip(RoundedCornerShape(4.dp))
                                                    .background(Color(0xFFE0F2FE))
                                                    .padding(horizontal = 6.dp, vertical = 2.dp)
                                            ) {
                                                Text(
                                                    catName,
                                                    fontSize = 10.sp,
                                                    fontWeight = FontWeight.Bold,
                                                    color = Color(0xFF0369A1)
                                                )
                                            }
                                        }

                                        IconButton(
                                            onClick = { viewModel.deleteMenuItem(item.id) },
                                            modifier = Modifier.size(24.dp)
                                        ) {
                                            Icon(
                                                Icons.Default.Delete,
                                                contentDescription = "Sil",
                                                tint = Color(0xFFEF4444),
                                                modifier = Modifier.size(18.dp)
                                            )
                                        }
                                    }

                                    Spacer(modifier = Modifier.height(12.dp))

                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Text(
                                            "${item.price} TL",
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 15.sp,
                                            color = Color(0xFF10B981)
                                        )
                                        Text(
                                            if (item.is_available) "Mevcut" else "Tükendi",
                                            fontSize = 11.sp,
                                            color = if (item.is_available) Color(0xFF059669) else Color.Red,
                                            fontWeight = FontWeight.Medium
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

